import { v4 as uuidv4 } from 'uuid';
import getApiConfig from '../config/apiConfig';

// Support Contract JSON Schema
const createEmptySupportContract = () => ({
  contractId: '',
  companyId: '',
  title: '',
  description: '',
  startDate: null,
  endDate: null,
  value: 0,
  status: 'Active',
  createdBy: '',
  createdDate: '',
  modifiedBy: '',
  modifiedDate: ''
});

// Helper function to handle API responses
const handleResponse = async (response) => {
  if (!response.ok) {
    const error = await response.text();
    throw new Error(error);
  }
  return response.json();
};

export const supportContractService = {
  // Create empty support contract
  createEmptySupportContract,

  // Validate configuration (no-op for API-based services)
  validateConfiguration() {
    // Support contract service uses backend API, no special configuration needed
    return true;
  },

  // Get all support contracts
  async getSupportContracts(pageSize = 10000) {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/supportcontracts?pageSize=${pageSize}&pageNumber=1`);
      const result = await handleResponse(response);
      
      // Normalize the data property names to match frontend expectations
      const normalizedData = result.data.map(contract => ({
        contractId: contract.Id,
        contractName: contract.ContractName,
        scopeOfSupport: contract.ScopeOfSupport,
        companyId: contract.CompanyId,
        companyName: contract.CompanyName,
        maxTicketsPerMonth: contract.MaxTicketsPerMonth,
        status: contract.Status,
        createdBy: contract.CreatedByEmail,
        createdDate: contract.CreatedDate ? new Date(contract.CreatedDate).toISOString().split('T')[0] : null,
        updatedBy: contract.UpdatedByEmail,
        updatedDate: contract.UpdatedDate ? new Date(contract.UpdatedDate).toISOString().split('T')[0] : null
      }));

      return normalizedData;
    } catch (error) {
      console.error('Error getting support contracts:', error);
      throw error;
    }
  },

  // Get support contract by ID
  async getSupportContract(contractId) {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/supportcontracts/${contractId}`);
      return handleResponse(response);
    } catch (error) {
      console.error('Error getting support contract:', error);
      throw error;
    }
  },

  // Get support contract by ID
  async getSupportContractById(contractId) {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/supportcontracts/${contractId}`);
      const result = await handleResponse(response);
      
      // Normalize the data to match frontend expectations
      return {
        contractId: result.Id,
        contractName: result.ContractName,
        scopeOfSupport: result.ScopeOfSupport,
        companyId: result.CompanyId,
        companyName: result.CompanyName,
        maxTicketsPerMonth: result.MaxTicketsPerMonth,
        status: result.Status,
        createdBy: result.CreatedByEmail,
        createdDate: result.CreatedDate ? new Date(result.CreatedDate).toISOString().split('T')[0] : null,
        updatedBy: result.UpdatedByEmail,
        updatedDate: result.UpdatedDate ? new Date(result.UpdatedDate).toISOString().split('T')[0] : null,
        contacts: result.contacts?.map(contact => ({
          contactId: contact.ContactId,
          firstName: contact.FirstName,
          lastName: contact.LastName,
          email: contact.Email,
          title: contact.Title
        })) || [],
        tickets: result.tickets?.map(ticket => ({
          id: ticket.Id,
          title: ticket.Title,
          ticketDetail: ticket.TicketDetail,
          status: ticket.Status,
          createdBy: ticket.CreatedByEmail,
          createdDate: ticket.CreatedDate ? new Date(ticket.CreatedDate).toISOString().split('T')[0] : null
        })) || []
      };
    } catch (error) {
      console.error('Error getting support contract:', error);
      throw error;
    }
  },

  // Save support contract (create or update)
  async saveSupportContract(contract, currentUserEmail) {
    try {
      const { apiBaseUrl } = getApiConfig();
      const isNew = !contract.contractId;
      
      // For new contracts, generate ID and set created info
      if (isNew) {
        contract.contractId = uuidv4();
        contract.createdBy = currentUserEmail;
        contract.createdDate = new Date().toISOString();
      } else {
        contract.modifiedBy = currentUserEmail;
        contract.modifiedDate = new Date().toISOString();
      }

      const method = isNew ? 'POST' : 'PUT';
      const url = isNew 
        ? `${apiBaseUrl}/supportcontracts`
        : `${apiBaseUrl}/supportcontracts/${contract.contractId}`;

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(contract)
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Error saving support contract:', error);
      throw error;
    }
  },

  // Delete support contract
  async deleteSupportContract(contractId) {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/supportcontracts/${contractId}`, {
        method: 'DELETE'
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error deleting support contract:', error);
      throw error;
    }
  },

  // =============================================
  // SUPPORT TICKET METHODS
  // =============================================

  // Create support ticket
  async createSupportTicket(contractId, ticketData) {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/supportcontracts/${contractId}/tickets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(ticketData)
      });
      const result = await handleResponse(response);
      // Normalize the response data
      return {
        id: result.Id,
        title: result.Title,
        ticketDetail: result.TicketDetail,
        status: result.Status,
        createdBy: result.CreatedByEmail,
        createdDate: result.CreatedDate ? new Date(result.CreatedDate).toISOString().split('T')[0] : null
      };
    } catch (error) {
      console.error('Error creating support ticket:', error);
      throw error;
    }
  },

  // Update support ticket
  async updateSupportTicket(ticketId, ticketData) {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/supportcontracts/tickets/${ticketId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(ticketData)
      });
      const result = await handleResponse(response);
      // Normalize the response data
      return {
        id: result.Id,
        title: result.Title,
        ticketDetail: result.TicketDetail,
        status: result.Status,
        createdBy: result.CreatedByEmail,
        createdDate: result.CreatedDate ? new Date(result.CreatedDate).toISOString().split('T')[0] : null
      };
    } catch (error) {
      console.error('Error updating support ticket:', error);
      throw error;
    }
  },

  // Delete support ticket
  async deleteSupportTicket(ticketId) {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/supportcontracts/tickets/${ticketId}`, {
        method: 'DELETE' });
      return handleResponse(response);
    } catch (error) {
      console.error('Error deleting support ticket:', error);
      throw error;
    }
  },

  // Get support ticket by ID
  async getSupportTicketById(ticketId) {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/supportcontracts/tickets/${ticketId}`);
      const result = await handleResponse(response);
      // Normalize the response data
      return {
        id: result.Id,
        title: result.Title,
        ticketDetail: result.TicketDetail,
        status: result.Status,
        createdBy: result.CreatedByEmail,
        createdDate: result.CreatedDate ? new Date(result.CreatedDate).toISOString().split('T')[0] : null
      };
    } catch (error) {
      console.error('Error getting support ticket:', error);
      throw error;
    }
  },

  // Add time entry to support contract
  async addTimeEntry(contractId, timeEntry, currentUserEmail) {
    try {
      const { apiBaseUrl } = getApiConfig();
      timeEntry.id = uuidv4();
      timeEntry.createdBy = currentUserEmail;
      timeEntry.createdDate = new Date().toISOString();

      const response = await fetch(`${apiBaseUrl}/supportcontracts/${contractId}/timeentries`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(timeEntry)
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Error adding time entry to support contract:', error);
      throw error;
    }
  },

  // Remove time entry from support contract
  async removeTimeEntry(contractId, timeEntryId) {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/supportcontracts/${contractId}/timeentries/${timeEntryId}`, {
        method: 'DELETE'
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error removing time entry from support contract:', error);
      throw error;
    }
  }
}; 